import { useCallback, useEffect, useRef, type ReactNode, type ReactElement } from 'react';

import { Button } from './Button.js';

/**
 * A dialog that a keyboard user can actually leave (criterion 26).
 *
 * Three things a hand-rolled modal usually omits, all of which make it unusable
 * rather than merely imperfect:
 *
 *   * **Focus moves in on open and back out on close.** Otherwise focus stays
 *     on the page behind, and a screen reader user is reading content they
 *     cannot see while a dialog they cannot find is open.
 *   * **Tab is trapped.** Without it, tabbing walks out of the dialog into the
 *     obscured page — visually the focus ring simply disappears.
 *   * **Escape closes.** The one shortcut everyone tries first.
 *
 * The backdrop click is deliberately NOT a close for `ConfirmDialog`'s
 * destructive use: a stray click should not be able to dismiss a confirmation.
 */
export interface ModalProps {
  open: boolean;
  title: string;
  onClose(): void;
  children: ReactNode;
  footer?: ReactNode;
  /** Set false for confirmations, where a stray click must not dismiss. */
  dismissOnBackdrop?: boolean;
}

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  dismissOnBackdrop = true,
}: ModalProps): ReactElement | null {
  const panel = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const focusables = useCallback((): HTMLElement[] => {
    if (!panel.current) return [];
    return Array.from(
      panel.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    );
  }, []);

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const first = focusables()[0] ?? panel.current;
    first?.focus();

    return () => {
      previouslyFocused.current?.focus();
    };
  }, [open, focusables]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = focusables();
      if (items.length === 0) return;
      const first = items[0] as HTMLElement;
      const last = items[items.length - 1] as HTMLElement;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, onClose, focusables]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/*
        `bg-scrim`, not `bg-black`. The scrim is the platform's own body ink, so
        the page reads as dimming *itself* while it waits for an answer rather
        than as a black sheet dropped over it by something external. It is
        deliberately not brandable: a backdrop tinted toward the tenant hue
        would change how urgent the same confirmation looks from one tenant to
        the next, and a confirmation's weight is a platform property.

        This element must stay the FIRST `aria-hidden` node in the dialog —
        `components.spec.tsx` finds the backdrop that way to prove a stray click
        cannot dismiss a destructive confirmation.
      */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-scrim/55 backdrop-blur-[2px]"
        onClick={dismissOnBackdrop ? onClose : undefined}
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        /*
         * The entrance is a `@starting-style` transition rather than a keyframe
         * animation, and that choice is a safety property, not a preference.
         * A keyframed entrance that fails to run — reduced motion, an engine
         * that skips it, a stylesheet that arrives late — leaves the panel on
         * its *from* state, which is a dialog nobody can see. Here the resting
         * state is the default and the starting state is the exception, so
         * every failure mode degrades to "appears instantly, fully visible".
         * The global reduced-motion rule caps the duration and removes the
         * transform, which lands it on exactly that.
         */
        className="relative z-10 flex max-h-[calc(100vh-4rem)] w-full max-w-lg flex-col overflow-hidden rounded-[var(--radius-lg)] border border-line-strong bg-surface opacity-100 shadow-[var(--elevation-3)] transition-[opacity,transform] duration-[var(--motion-base)] ease-[var(--ease-out)] starting:scale-[0.98] starting:opacity-0"
      >
        {/* Hairlines, so the body can scroll between a title and a footer that
            both stay put — a confirmation whose buttons scroll out of reach on
            a short viewport is a confirmation that cannot be answered. */}
        <h2 className="shrink-0 border-b border-line px-6 py-4 text-[length:var(--text-lg)] font-semibold tracking-[var(--tracking-tight)] text-body">
          {title}
        </h2>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 text-[length:var(--text-sm)] text-body">
          {children}
        </div>
        <div className="flex shrink-0 justify-end gap-[var(--space-2)] border-t border-line px-6 py-4">
          {footer ?? (
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * A confirmation whose default is "do nothing".
 *
 * `Cancel` is the secondary button but it is the one focus lands on, and the
 * backdrop does not dismiss — the two together mean an accidental Enter or
 * click cannot disconnect a SIM.
 *
 * Nothing here is decorated with the brand. A dialog whose job is to look like
 * the platform's own warning may not follow the tenant, in the same way and for
 * the same reason the `danger` variant may not (§11.6).
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  destructive = false,
  onConfirm,
  onCancel,
  busy = false,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm(): void;
  onCancel(): void;
  busy?: boolean;
}): ReactElement | null {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onCancel}
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} autoFocus>
            Cancel
          </Button>
          <Button variant={destructive ? 'danger' : 'primary'} loading={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p>{body}</p>
    </Modal>
  );
}
